import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { GameCard } from '@/components/common/GameCard';
import { SEO } from '@/components/common/SEO';

const AllGamesPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [engagementScores, setEngagementScores] = useState({});

  const isUpcoming = type === 'upcoming';
  const title = isUpcoming ? 'Upcoming Livestreams' : 'Replays';
  const badgeText = isUpcoming ? 'Coming Soon' : 'Watch Again';
  const badgeClass = isUpcoming ? 'bg-sky-500 text-white' : 'bg-gray-600 text-white';

  useEffect(() => {
    const loadGames = async () => {
      setLoading(true);
      try {
        const res = isUpcoming
          ? await api.getUpcomingGames(100)
          : await api.getReplayGames(100);
        setGames(res.data);
        try { const e = await api.getEngagementScores(); setEngagementScores(e.data.scores || {}); } catch {}
      } catch (error) {
        console.error('Error loading games:', error);
      } finally {
        setLoading(false);
      }
    };
    loadGames();
  }, [isUpcoming]);

  return (
    <div className="min-h-screen py-8 px-4" data-testid={`all-${type}-page`}>
      <SEO
        title={title}
        description={isUpcoming ? 'Browse all upcoming live streams.' : 'Browse all match replays.'}
        url={`/games/${type}`}
      />
      <div className="max-w-7xl mx-auto">
        <Link to="/" className="inline-flex items-center text-sky-400 hover:text-sky-300 text-sm mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-3xl font-bold text-white">{title}</h1>
          <Badge className={badgeClass}>{badgeText}</Badge>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="game-card animate-pulse">
                <div className="aspect-video bg-gray-700 rounded-t-xl" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-700 rounded w-1/2" />
                  <div className="h-5 bg-gray-700 rounded w-3/4" />
                  <div className="h-4 bg-gray-700 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {games.map(game => (
                <GameCard key={game.id} game={game} onWatch={() => navigate(`/watch/${game.id}`)} />
              ))}
            </div>
            {games.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg">
                  {isUpcoming ? 'No upcoming livestreams scheduled.' : 'No replays available yet.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default AllGamesPage;
